import { isFormulaJson } from '@doss/common-all'
import {
  FloatingPortal,
  autoUpdate,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { Box, Theme, Typography } from '@mui/material'
import { CSSProperties, useEffect } from 'react'
import { PiFunctionBold } from 'react-icons/pi'
import { useGetFormulaPopoverLazyQuery } from '~/common/generated/apollo-operations'
import useStyles from '~/common/hooks/useStyles'
import { formatLogicExpressionForDisplay } from '~/components/logic-expression/utils'
import { FormulaExpression } from './FormulaExpression'
import { FormulaProvider } from './FormulaProvider'
import { buildFieldNameMap } from './utils'

function getStyles(theme: Theme) {
  return {
    root: {
      display: 'flex',
      backgroundColor: theme.palette.common.doss.gray[1],
      padding: theme.spacing(1),
      alignItems: 'center',
      border: `1px solid ${theme.palette.common.doss.gray[4]}`,
      borderRadius: '2px',
    },
    icon: {
      display: 'flex',
      marginInlineEnd: theme.spacing(1),
      color: theme.palette.common.doss.gray[10],
    },
    formula: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      border: `1px solid ${theme.palette.common.doss.gray[4]}`,
      padding: theme.spacing(1),
      overflow: 'auto',
    },
    popover: {
      backgroundColor: theme.palette.common.doss.gray[1],
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(0.5),
    },
  }
}

interface FormulaPopoverProps {
  onClose: () => void
  anchorEl: HTMLElement | null
  formulaId?: string
  align: 'top' | 'right'
  tableId: string
}

export function FormulaPopover({
  onClose,
  anchorEl,
  formulaId,
  align,
  tableId,
}: FormulaPopoverProps) {
  const styles = useStyles(getStyles)
  const [getFormulaPopover, result] = useGetFormulaPopoverLazyQuery()

  useEffect(() => {
    if (formulaId) {
      getFormulaPopover({ variables: { formulaId } })
    }
  }, [formulaId, getFormulaPopover])

  const { refs, floatingStyles, isPositioned, context } = useFloating({
    open: true,
    onOpenChange: (open) => {
      if (!open) {
        onClose()
      }
    },
    strategy: 'fixed',
    placement: align === 'top' ? 'top-start' : 'right-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(align === 'top' ? 10 : 16), shift({ padding: 8 })],
    elements: { reference: anchorEl },
  })

  const dismiss = useDismiss(context)
  const { getFloatingProps } = useInteractions([dismiss])

  const formulaString = result.data?.formula.formulaString
  const formulaJsonRaw = result.data?.formulaJson
  const formulaJson = formulaJsonRaw && isFormulaJson(formulaJsonRaw) ? formulaJsonRaw : null

  if (!formulaString && !formulaJson) {
    return null
  }

  const fieldNameMap = formulaJson ? buildFieldNameMap(formulaJson) : {}
  const displayFormulaParts = formulaString
    ? formatLogicExpressionForDisplay(formulaString, fieldNameMap)
    : null

  const composedStyle: CSSProperties = {
    ...floatingStyles,
    zIndex: 1300,
    visibility: isPositioned ? 'visible' : 'hidden',
    borderRadius: '4px',
    boxShadow:
      '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
  }

  return (
    <FloatingPortal>
      <div ref={refs.setFloating} style={composedStyle} {...getFloatingProps()}>
        <Box className="drag-handle" sx={styles.popover}>
          <Typography variant="body2">Formula</Typography>
        </Box>
        <Box sx={styles.root}>
          <Box sx={styles.icon}>
            <PiFunctionBold />
          </Box>
          <Box sx={styles.formula}>
            <Typography variant="body2">=</Typography>
            <Typography
              component="span"
              sx={{
                display: 'inline-flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 0.5,
              }}
              variant="body2"
            >
              {formulaString
                ? displayFormulaParts
                : formulaJson != null && (
                    <FormulaProvider formula={formulaJson} tableId={tableId}>
                      <FormulaExpression data={formulaJson} />
                    </FormulaProvider>
                  )}
            </Typography>
          </Box>
        </Box>
      </div>
    </FloatingPortal>
  )
}
